import api from '@/shared/api/client';

/**
 * Appels de la console de plateforme, réservés au super admin.
 *
 * Aucun de ces appels ne renvoie de donnée métier : que des sous-comptes, des
 * comptes utilisateurs et des nombres. Le cloisonnement est tenu côté serveur
 * (`get_tenant_context` refuse explicitement le super admin), ce module ne fait
 * que refléter cette frontière.
 */

const lire = (chemin) => api.get(chemin).then((r) => r.data);

export const getOverview = () => lire('/superadmin/overview');

/**
 * Journal de plateforme, filtré côté serveur.
 *
 * Les trois filtres sont passés au serveur et non appliqués après coup : le journal est
 * borné à 500 lignes en lecture, donc filtrer localement ne montrerait que ce qui tient
 * dans la fenêtre déjà chargée, et manquerait justement les lignes anciennes qu'on
 * cherche quand on filtre.
 */
export const getJournal = ({ limite = 100, action = '', cibleId = '', niveau = '',
                             recherche = '' } = {}) => {
  const params = new URLSearchParams({ limite: String(limite) });
  if (action) params.set('action', action);
  if (cibleId) params.set('cible_id', cibleId);
  if (niveau) params.set('niveau', niveau);
  if (recherche) params.set('recherche', recherche);
  return lire(`/superadmin/journal?${params.toString()}`);
};

export const getSousComptes = () => lire('/tenants');
export const getSousCompte = (id) => lire(`/tenants/${id}`);

export const creerSousCompte = (name) =>
  api.post('/tenants', { name }).then((r) => r.data);

export const majSousCompte = (id, patch) =>
  api.put(`/tenants/${id}`, patch).then((r) => r.data);

export const creerAdmin = (id, { email, name, password }) =>
  api.post(`/tenants/${id}/admins`, { email, name, password }).then((r) => r.data);

/**
 * Ouvre une session d'observation sur un sous-compte.
 *
 * Le serveur delivre un jeton en LECTURE SEULE, valable une heure, qui emprunte
 * l'identite d'un administrateur du sous-compte : c'est le seul moyen de voir
 * exactement ses ecrans, qui dependent du role. La route existait et n'etait
 * appelee par aucun ecran.
 */
export const observerSousCompte = (id) =>
  api.post(`/superadmin/impersonate/${id}`).then((r) => r.data);

/**
 * Supprime un sous-compte vide.
 *
 * Le serveur refuse dans deux cas qu'il faut afficher tels quels : le sous-compte par
 * défaut (400) et un sous-compte qui porte encore des données (409, avec le détail des
 * volumes). Cette route existait et n'était appelée par aucun écran.
 */
export const supprimerSousCompte = (id) =>
  api.delete(`/tenants/${id}`).then((r) => r.data);

/**
 * Libellés lisibles des actions du journal de plateforme.
 *
 * Doit couvrir TOUTES les actions écrites par le serveur. Les cinq actions ajoutées par
 * la console (observation, gestion des comptes, suppression de sous-compte) s'affichaient
 * en clé technique brute, `user.reset_password` en plein journal : les gestes les plus
 * sensibles étaient les moins lisibles.
 */
export const LIBELLES_ACTION = {
  'tenant.create': 'Sous-compte créé',
  'tenant.rename': 'Sous-compte renommé',
  'tenant.suspend': 'Sous-compte suspendu',
  'tenant.reactivate': 'Sous-compte réactivé',
  'tenant.admin_create': 'Administrateur ajouté',
  'tenant.delete': 'Sous-compte supprimé',
  'tenant.observation': 'Entrée en observation',
  'user.create': 'Compte créé',
  'user.update': 'Compte modifié',
  'user.reset_password': 'Mot de passe réinitialisé',
  'user.delete': 'Compte supprimé',
  'auth.login': 'Connexion',
  'auth.login_refuse': 'Connexion refusée',
};

/** Libellés et teintes des niveaux de gravité, alignés sur `NIVEAUX_ACTION` du serveur. */
export const NIVEAUX = {
  info: { libelle: 'Info', teinte: 'bg-slate-100 text-slate-600' },
  avertissement: { libelle: 'Avertissement', teinte: 'bg-amber-100 text-amber-700' },
  critique: { libelle: 'Critique', teinte: 'bg-red-100 text-red-700' },
};

/** Ce qu'un type de cible désigne, pour ne pas ranger un compte sous « sous-compte ». */
export const LIBELLES_CIBLE = { tenant: 'Sous-compte', user: 'Compte' };
